import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  fetchSubscribers,
  fetchSubscriberSummary,
  fetchSubscriberReferrers,
  createReferrer,
  renameReferrer,
  updateReferrerStatus,
  createSubscriber,
  updateSubscriber,
  renewSubscriber,
  deleteSubscriber,
  convertFreeTrial,
  readApiErrorMessage,
} from "../../services/apiClient";
import ConfirmDialog from "../../components/ConfirmDialog";
import RenewSubscriptionDialog from "../../components/RenewSubscriptionDialog";
import "./AdminUsers.css";

/* Body cells carry a `data-label` matching their column heading: inert on a
   desktop, but below 760px AdminUsers.css turns each row into a card and
   renders the label beside the value, so none of the ten columns is lost to a
   sideways scroll on a phone. */

const EMPTY_FORM = {
  full_name: "",
  phone_number: "",
  referrer_id: "",
  referrer_commission: "",
  subscription_amount: "",
  account_deposit: "",
  subscription_start_date: "",
  subscription_duration_months: "1",
  notes: "",
};

function AdminSubscribers() {
  const location = useLocation();
  const navigate = useNavigate();

  /* Arriving from "Convert to Subscriber" on the Free Trials page. The trial
     supplies the name and phone it knows; everything the subscription needs —
     referrer, amount, dates — is filled in here, on the ordinary form, using
     the ordinary validation. Submitting posts to the conversion endpoint,
     which creates the subscriber through the same service as a direct add and
     only then marks the trial converted. */
  const conversion = (location.state || {}).convertFreeTrial || null;

  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [activeReferrers, setActiveReferrers] = useState([]);
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [form, setForm] = useState(
    conversion
      ? { ...EMPTY_FORM, full_name: conversion.full_name, phone_number: conversion.phone_number }
      : EMPTY_FORM
  );
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [renewRequest, setRenewRequest] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const [showReferrers, setShowReferrers] = useState(false);
  const [newReferrerName, setNewReferrerName] = useState("");
  // Optional at creation. A referrer only needs an address if they are
  // going to sign in and see My Team; most never do.
  const [newReferrerEmail, setNewReferrerEmail] = useState("");
  const [editingReferrer, setEditingReferrer] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filters.search.trim()) params.search = filters.search.trim();
      if (filters.status) params.status = filters.status;
      const data = await fetchSubscribers(params);
      setSubscribers(data.subscribers || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (loadError) {
      setError(readApiErrorMessage(loadError, "Could not load subscribers."));
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  /* Deliberately not part of `load`. The totals describe every subscriber on
     record, so they are fetched once and refreshed when the records actually
     change — never when the table's search or status filter changes, which
     would silently turn an overall figure into a filtered one. */
  const loadSummary = useCallback(async () => {
    try {
      const data = await fetchSubscriberSummary();
      setSummary(data.summary || null);
    } catch {
      // The table is the page's job; a failed total shows as unavailable
      // rather than blanking the records beside it.
      setSummary(null);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const loadReferrers = useCallback(async () => {
    try {
      const [allData, activeData] = await Promise.all([
        fetchSubscriberReferrers(),
        fetchSubscriberReferrers({ activeOnly: true }),
      ]);
      const options = allData.referrers || [];
      const activeOptions = activeData.referrers || [];
      setReferrers(options);
      setActiveReferrers(activeOptions);
      setForm((current) => ({
        ...current,
        referrer_id: current.referrer_id || (activeOptions[0] || {}).id || "",
      }));
    } catch (loadError) {
      setError(readApiErrorMessage(loadError, "Could not load referrers."));
    }
  }, []);

  useEffect(() => { loadReferrers(); }, [loadReferrers]);

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  /**
   * The commission belongs to the referral, not to the subscriber.
   *
   * Losing the referrer therefore has to lose the money with it, or an amount
   * agreed with one person is left sitting in the field while nobody is named
   * — and the server, which zeroes a commission it has no referrer for, would
   * disagree with what the form is showing.
   */
  function changeReferrer(value) {
    setForm((current) => ({
      ...current,
      referrer_id: value,
      referrer_commission: value ? current.referrer_commission : "",
    }));
  }

  function resetForm() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, referrer_id: (activeReferrers[0] || {}).id || "" });
  }

  /** Drops the conversion context without leaving the page. */
  function cancelConversion() {
    navigate(location.pathname, { replace: true, state: null });
    resetForm();
  }

  async function save(event) {
    event.preventDefault();
    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        ...form,
        subscription_amount: Number(form.subscription_amount),
        // Blank means no commission was agreed, which the server stores as 0.
        // It is sent beside the subscription amount and never taken out of it.
        referrer_commission: form.referrer_commission === "" ? null : Number(form.referrer_commission),
        account_deposit: form.account_deposit === "" ? null : Number(form.account_deposit),
        subscription_duration_months: Number(form.subscription_duration_months),
      };
      if (editingId) {
        await updateSubscriber(editingId, payload);
        setNotice("Subscriber updated.");
      } else if (conversion) {
        // One call: the backend creates the subscriber with the service used
        // below, then marks the free trial converted. A rejected payload
        // leaves the trial untouched and still convertible.
        await convertFreeTrial(conversion.free_trial_id, payload);
        setNotice(`${payload.full_name} was added and the free trial is now converted.`);
        navigate(location.pathname, { replace: true, state: null });
      } else {
        await createSubscriber(payload);
        setNotice("Subscriber added.");
      }
      resetForm();
      await Promise.all([load(), loadSummary()]);
    } catch (saveError) {
      setError(readApiErrorMessage(saveError, "The subscriber could not be saved."));
    } finally {
      setIsSaving(false);
    }
  }

  function edit(subscriber) {
    setEditingId(subscriber.id);
    setForm({
      full_name: subscriber.full_name,
      phone_number: subscriber.phone_number,
      referrer_id: subscriber.referrer_id,
      // A stored zero is "no commission", so the field opens empty rather than
      // showing a 0 the administrator then has to clear.
      referrer_commission: subscriber.referrer_commission ? String(subscriber.referrer_commission) : "",
      subscription_amount: String(subscriber.subscription_amount),
      account_deposit: subscriber.account_deposit === null ? "" : String(subscriber.account_deposit),
      subscription_start_date: subscriber.subscription_start_date,
      subscription_duration_months: String(subscriber.subscription_duration_months),
      notes: subscriber.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function addReferrer(event) {
    event.preventDefault();
    try {
      const data = await createReferrer({
        name: newReferrerName,
        email: newReferrerEmail.trim() || null,
      });
      setNewReferrerName("");
      setNewReferrerEmail("");
      setForm((current) => ({ ...current, referrer_id: data.referrer.id }));
      setNotice(`${data.referrer.name} was added and is ready to select.`);
      setError(null);
      await loadReferrers();
    } catch (saveError) {
      setError(readApiErrorMessage(saveError, "The referrer could not be added."));
    }
  }

  async function saveReferrerName(event) {
    event.preventDefault();
    try {
      const data = await renameReferrer(editingReferrer.id, {
        name: editingReferrer.name,
        email: (editingReferrer.email || "").trim() || null,
      });
      setEditingReferrer(null);
      setNotice(`Referrer renamed to ${data.referrer.name}.`);
      setError(null);
      // The commission breakdown is listed by name, so it is refreshed too.
      await Promise.all([loadReferrers(), load(), loadSummary()]);
    } catch (saveError) {
      setError(readApiErrorMessage(saveError, "The referrer could not be renamed."));
    }
  }

  async function toggleReferrer(referrer) {
    try {
      await updateReferrerStatus(referrer.id, !referrer.active);
      if (referrer.active && !editingId && form.referrer_id === referrer.id) {
        const replacement = activeReferrers.find((item) => item.id !== referrer.id);
        // Same rule as clearing the select by hand: no referrer, no commission.
        changeReferrer(replacement ? replacement.id : "");
      }
      setNotice(`${referrer.name} is now ${referrer.active ? "inactive" : "active"}.`);
      setError(null);
      await loadReferrers();
    } catch (saveError) {
      setError(readApiErrorMessage(saveError, "The referrer status could not be changed."));
    }
  }

  /* The renewed subscriber comes back from the API, so the row is replaced in
     place and the new expiry and status appear without a manual refresh. A
     status filter is the one case that needs the list refetched: a row renewed
     out of the "Expired" view no longer belongs in it. The totals are left
     alone — a renewal moves an expiry date and no money at all. */
  async function confirmRenewal(months) {
    const request = renewRequest;
    if (!request) return;
    setIsRenewing(true);
    setError(null);
    setNotice(null);
    try {
      const { subscriber: renewed } = await renewSubscriber(request.id, months);
      setSubscribers((current) => current.map((item) => (item.id === renewed.id ? renewed : item)));
      setNotice(
        `${renewed.full_name} was renewed for ${months} ${months === 1 ? "month" : "months"}. New expiry: ${renewed.subscription_expiry_date}.`
      );
      if (filters.status) await load();
    } catch (renewError) {
      setError(readApiErrorMessage(renewError, "The subscription could not be renewed."));
    } finally {
      // Closed either way: the error banner sits on the page behind the dialog.
      setRenewRequest(null);
      setIsRenewing(false);
    }
  }

  async function confirmDelete() {
    const request = deleteRequest;
    setDeleteRequest(null);
    if (!request) return;
    try {
      await deleteSubscriber(request.id);
      if (editingId === request.id) resetForm();
      setNotice(`${request.name} was deleted.`);
      setError(null);
      await Promise.all([load(), loadSummary()]);
    } catch (deleteError) {
      setError(readApiErrorMessage(deleteError, "The subscriber could not be deleted."));
    }
  }

  const currentInactiveReferrer = editingId
    ? referrers.find((item) => item.id === form.referrer_id && !item.active)
    : null;
  const selectableReferrers = currentInactiveReferrer
    ? [currentInactiveReferrer, ...activeReferrers]
    : activeReferrers;
  const isFiltered = Boolean(filters.search.trim() || filters.status);

  return (
    <main className="admin-page">
      <section className="admin-workspace">
        <header className="admin-topbar">
          <div><span className="admin-kicker">Administration</span><strong>Subscribers</strong></div>
          <span className="admin-count">{total} {total === 1 ? "subscriber" : "subscribers"}</span>
        </header>

        <div className="admin-content">
          <nav className="admin-tabs" aria-label="Administration sections">
            <Link to="/admin/users">Users</Link>
            <Link className="is-active" to="/admin/subscribers">Subscribers</Link>
            <Link to="/admin/free-trials">Free Trials</Link>
          </nav>

          <div className="admin-section-actions">
            <button className="admin-btn" type="button" onClick={() => setShowReferrers((value) => !value)}>
              {showReferrers ? "Close Referrers" : "Manage Referrers"}
            </button>
          </div>

          {showReferrers ? (
            <ReferrerManager
              newEmail={newReferrerEmail}
              onNewEmail={setNewReferrerEmail}
              referrers={referrers}
              newName={newReferrerName}
              onNewName={setNewReferrerName}
              onAdd={addReferrer}
              editing={editingReferrer}
              onEditing={setEditingReferrer}
              onRename={saveReferrerName}
              onToggle={toggleReferrer}
            />
          ) : null}

          <form className="admin-card admin-add" onSubmit={save}>
            <h2>
              {editingId
                ? "Edit subscriber"
                : conversion
                  ? "Convert free trial to subscriber"
                  : "Add subscriber"}
            </h2>
            {conversion && !editingId ? (
              <p className="admin-banner is-ok admin-conversion">
                <span>
                  Converting the free trial for <strong>{conversion.full_name}</strong>
                  {conversion.email ? ` (${conversion.email})` : ""}. Fill in the subscription
                  details below; the trial is marked converted once this subscriber is saved.
                </span>
                <button className="admin-btn" type="button" onClick={cancelConversion}>
                  Cancel conversion
                </button>
              </p>
            ) : null}

            {/* Three groups rather than one eight-cell grid: who the subscriber
                is, who referred them and what that referral is owed, and what
                the subscription itself is worth. The two money pairs sit in
                different groups on purpose — a commission is paid out to the
                referrer, an amount and a deposit belong to the subscriber. */}
            <fieldset className="admin-fieldset">
              <legend>Subscriber</legend>
              <div className="admin-fieldset__grid admin-fieldset__grid--two">
                <Field label="Full name"><input required value={form.full_name} onChange={(e) => change("full_name", e.target.value)} /></Field>
                <Field label="Phone number"><input required type="tel" value={form.phone_number} onChange={(e) => change("phone_number", e.target.value)} /></Field>
              </div>
            </fieldset>

            <fieldset className="admin-fieldset">
              <legend>Referral</legend>
              <div className="admin-fieldset__grid admin-fieldset__grid--two">
                <Field label="Referred by">
                  <select required value={form.referrer_id} onChange={(e) => changeReferrer(e.target.value)}>
                    <option value="" disabled>{activeReferrers.length ? "Select referrer" : "Add an active referrer first"}</option>
                    {selectableReferrers.map((referrer) => (
                      <option key={referrer.id} value={referrer.id}>{referrer.name}{referrer.active ? "" : " (Inactive)"}</option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Referrer commission (optional)"
                  hint="Paid to the referrer. Held separately — it is never deducted from the subscription amount."
                >
                  <input
                    min="0"
                    step="0.01"
                    type="number"
                    inputMode="decimal"
                    placeholder="0.00"
                    disabled={!form.referrer_id}
                    value={form.referrer_commission}
                    onChange={(e) => change("referrer_commission", e.target.value)}
                  />
                </Field>
              </div>
            </fieldset>

            <fieldset className="admin-fieldset">
              <legend>Subscription</legend>
              <div className="admin-fieldset__grid admin-fieldset__grid--four">
                <Field label="Subscription amount"><input required min="0" step="0.01" type="number" inputMode="decimal" value={form.subscription_amount} onChange={(e) => change("subscription_amount", e.target.value)} /></Field>
                <Field label="Account deposit (optional)" hint="Trading-account information only. Never applied to the subscription amount.">
                  <input min="0" step="0.01" type="number" inputMode="decimal" value={form.account_deposit} onChange={(e) => change("account_deposit", e.target.value)} />
                </Field>
                <Field label="Start date"><input required type="date" value={form.subscription_start_date} onChange={(e) => change("subscription_start_date", e.target.value)} /></Field>
                <Field label="Duration">
                  <select value={form.subscription_duration_months} onChange={(e) => change("subscription_duration_months", e.target.value)}>
                    {[1, 3, 6, 12].map((months) => <option key={months} value={months}>{months} {months === 1 ? "month" : "months"}</option>)}
                  </select>
                </Field>
              </div>
              <div className="admin-fieldset__grid">
                <Field label="Notes (optional)"><textarea rows="3" value={form.notes} onChange={(e) => change("notes", e.target.value)} /></Field>
              </div>
            </fieldset>

            <div className="admin-actions">
              <button className="admin-btn admin-btn--primary" disabled={isSaving} type="submit">{isSaving ? "Saving…" : editingId ? "Save changes" : conversion ? "Create subscriber & convert" : "Add subscriber"}</button>
              {editingId ? <button className="admin-btn" type="button" onClick={resetForm}>Cancel</button> : null}
            </div>
          </form>

          {notice ? <p className="admin-banner is-ok">{notice}</p> : null}
          {error ? <p className="admin-banner is-error">{error}</p> : null}

          <div className="admin-card">
            <div className="admin-filters admin-filters--subscribers">
              <input type="search" placeholder="Search name or phone" aria-label="Search subscribers" value={filters.search} onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))} />
              <select aria-label="Filter subscription status" value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}>
                <option value="">All statuses</option><option value="ACTIVE">Active</option><option value="EXPIRED">Expired</option>
              </select>
            </div>
            <div className="admin-table-wrap">
              <table className="admin-table admin-table--subscribers">
                <thead><tr><th>Name</th><th>Phone</th><th>Referred By</th><th>Subscription</th><th>Commission</th><th>Account Deposit</th><th>Start Date</th><th>Expiry Date</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {isLoading ? <tr><td colSpan={10} className="admin-empty">Loading…</td></tr> : subscribers.length === 0 ? <tr><td colSpan={10} className="admin-empty">No subscribers match these filters.</td></tr> : subscribers.map((subscriber) => (
                    <tr key={subscriber.id}>
                      <td data-label="Name"><div className="admin-user"><strong>{subscriber.full_name}</strong>{subscriber.notes ? <span title={subscriber.notes}>Has notes</span> : null}</div></td>
                      <td data-label="Phone">{subscriber.phone_number}</td><td data-label="Referred By">{subscriber.referred_by}</td>
                      <td data-label="Subscription">{money(subscriber.subscription_amount)}</td>
                      {/* No commission is a dash, not $0.00: a column of zeroes
                          reads as data when it is the absence of it. */}
                      <td data-label="Commission">{subscriber.referrer_commission ? <span className="admin-money-out">{money(subscriber.referrer_commission)}</span> : <span className="admin-none">—</span>}</td>
                      <td data-label="Account Deposit">{subscriber.account_deposit === null ? <span className="admin-none">—</span> : money(subscriber.account_deposit)}</td>
                      <td data-label="Start Date">{subscriber.subscription_start_date}</td><td data-label="Expiry Date">{subscriber.subscription_expiry_date}</td>
                      <td data-label="Status"><span className={`admin-badge is-${subscriber.subscription_status === "ACTIVE" ? "on" : "off"}`}>{subscriber.subscription_status}</span></td>
                      <td data-label="Actions"><div className="admin-actions"><button className="admin-btn admin-btn--renew" type="button" onClick={() => setRenewRequest({ id: subscriber.id, name: subscriber.full_name, currentExpiry: subscriber.subscription_expiry_date, status: subscriber.subscription_status })}>Renew</button><button className="admin-btn" type="button" onClick={() => edit(subscriber)}>Edit</button><button className="admin-btn admin-btn--danger" type="button" onClick={() => setDeleteRequest({ id: subscriber.id, name: subscriber.full_name })}>Delete</button></div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <FinancialSummary summary={summary} isFiltered={isFiltered} listedCount={subscribers.length} />
        </div>
      </section>
      <RenewSubscriptionDialog request={renewRequest} isSaving={isRenewing} onConfirm={confirmRenewal} onCancel={() => setRenewRequest(null)} />
      <ConfirmDialog request={deleteRequest ? { title: "Delete subscriber", body: `Delete ${deleteRequest.name}? This cannot be undone.`, confirmLabel: "Delete", tone: "danger" } : null} onConfirm={confirmDelete} onCancel={() => setDeleteRequest(null)} />
    </main>
  );
}

/**
 * The financial position across every subscriber on record.
 *
 * Read from the backend's own aggregation, never recomputed from the rows in
 * the table above: those rows are paginated and filtered, and adding them up
 * would produce a number that quietly changed whenever somebody typed in the
 * search box. When a filter is active the section says so in as many words,
 * so "total" is never ambiguous.
 */
export function FinancialSummary({ summary, isFiltered, listedCount }) {
  if (!summary) return null;

  const breakdown = summary.referrer_commissions || [];
  // The widest payout sets the scale, so the bars compare referrers with each
  // other rather than against an arbitrary ceiling.
  const largest = breakdown.reduce((max, row) => Math.max(max, row.total_commission), 0);

  return (
    <section className="admin-card admin-summary" aria-labelledby="subscriber-financials">
      <header className="admin-summary__head">
        <h2 id="subscriber-financials">Subscriber totals</h2>
        <p className="admin-summary__scope">
          Across all {summary.subscriber_count} {summary.subscriber_count === 1 ? "subscriber" : "subscribers"} on record.
          {isFiltered ? ` The table above is filtered and is showing ${listedCount} of them.` : ""}
        </p>
      </header>

      <div className="admin-summary__metrics">
        <Metric
          label="Total subscription amount"
          value={money(summary.total_subscription_amount)}
          tone="gold"
          note="What subscribers have been charged."
        />
        <Metric
          label="Total referrer commissions"
          value={money(summary.total_referrer_commission)}
          note={
            breakdown.length
              ? `Owed across ${breakdown.length} ${breakdown.length === 1 ? "referrer" : "referrers"}.`
              : "No commissions recorded."
          }
        />
        <Metric
          label="Account deposits tracked"
          value={money(summary.total_account_deposit)}
          tone="muted"
          note="Trading capital. Not revenue, and never part of the amounts above."
        />
      </div>

      <div className="admin-summary__breakdown">
        <h3>Referrer commissions</h3>
        {breakdown.length === 0 ? (
          <p className="admin-hint admin-hint--block">
            No referrer has earned a commission yet. Referrers appear here once a
            subscriber is recorded against them with a commission above zero.
          </p>
        ) : (
          <ul className="admin-payouts">
            {breakdown.map((row) => (
              <li key={row.referrer_id || `name:${row.referrer_name}`}>
                <span className="admin-payouts__who">
                  <span className="admin-payouts__name">{row.referrer_name}</span>
                  <span className="admin-payouts__count">
                    {row.subscriber_count} {row.subscriber_count === 1 ? "subscriber" : "subscribers"}
                  </span>
                </span>
                <span className="admin-payouts__amount">{money(row.total_commission)}</span>
                <span
                  className="admin-payouts__bar"
                  aria-hidden="true"
                  style={{ "--share": largest ? `${(row.total_commission / largest) * 100}%` : "0%" }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function Metric({ label, value, note, tone }) {
  return (
    <div className={`admin-metric${tone ? ` is-${tone}` : ""}`}>
      <span className="admin-metric__label">{label}</span>
      <strong className="admin-metric__value">{value}</strong>
      {note ? <span className="admin-metric__note">{note}</span> : null}
    </div>
  );
}

function Field({ label, wide, hint, children }) {
  return (
    <label className={wide ? "admin-field--wide" : undefined}>
      <span>{label}</span>
      {children}
      {hint ? <span className="admin-field__hint">{hint}</span> : null}
    </label>
  );
}

function ReferrerManager({ referrers, newName, onNewName, newEmail, onNewEmail, onAdd, editing, onEditing, onRename, onToggle }) {
  return (
    <section className="admin-card">
      <h2>Manage Referrers</h2>
      <p className="admin-hint admin-hint--block">
        An email is only needed for a referrer who will sign in. Invite that
        address as a Marketing user and their My Team links itself on first
        sign-in — no further setup.
      </p>
      <form className="admin-referrer-add" onSubmit={onAdd}>
        <input required value={newName} placeholder="New referrer name" aria-label="New referrer name" onChange={(event) => onNewName(event.target.value)} />
        <input type="email" value={newEmail} placeholder="Sign-in email (optional)" aria-label="New referrer sign-in email" onChange={(event) => onNewEmail(event.target.value)} />
        <button className="admin-btn admin-btn--primary" type="submit">+ Add Referrer</button>
      </form>
      <div className="admin-table-wrap">
        <table className="admin-table admin-table--referrers">
          <thead><tr><th>Name</th><th>Sign-in email</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {referrers.length === 0 ? <tr><td colSpan={4} className="admin-empty">No referrers yet.</td></tr> : referrers.map((referrer) => (
              <tr key={referrer.id}>
                <td data-label="Name">
                  {editing && editing.id === referrer.id ? (
                    <form className="admin-inline-edit" onSubmit={onRename}>
                      <input required aria-label={`Rename ${referrer.name}`} value={editing.name} onChange={(event) => onEditing({ ...editing, name: event.target.value })} />
                      <input type="email" aria-label={`Sign-in email for ${referrer.name}`} placeholder="Sign-in email" value={editing.email || ""} onChange={(event) => onEditing({ ...editing, email: event.target.value })} />
                      <button className="admin-btn" type="submit">Save</button>
                      <button className="admin-btn" type="button" onClick={() => onEditing(null)}>Cancel</button>
                    </form>
                  ) : referrer.name}
                </td>
                <td data-label="Sign-in email">
                  {referrer.email ? (
                    <span className="admin-sub">
                      {referrer.email}
                      {referrer.linked ? <span className="admin-tag">Linked</span> : null}
                    </span>
                  ) : (
                    <span className="admin-hint">Not set</span>
                  )}
                </td>
                <td data-label="Status"><span className={`admin-badge is-${referrer.active ? "on" : "off"}`}>{referrer.active ? "Active" : "Inactive"}</span></td>
                <td data-label="Actions"><div className="admin-actions"><button className="admin-btn" type="button" onClick={() => onEditing({ id: referrer.id, name: referrer.name, email: referrer.email || "" })}>Edit</button><button className="admin-btn" type="button" onClick={() => onToggle(referrer)}>{referrer.active ? "Deactivate" : "Activate"}</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function money(value) {
  return Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export default AdminSubscribers;
